import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { withRouter } from 'core/libs/router';

import withTheme from 'core/hocs/withTheme';
import withBreakpoint from 'core/hocs/withBreakpoint';

import Button from 'core/components/Button';

import DoubleArrowLeft from 'core/components/Icon/DoubleArrowLeft';
import DoubleArrowRight from 'core/components/Icon/DoubleArrowRight';

import themePropTypes from 'core/utils/prop-types/theme';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { pageLinkConstructor } from 'core/utils/url-helper';

import Control from './Control';


/**
 * @author Aleksey Dibrivniy <a.dibrivniy@rambler-co.ru>
 */

function Paginatron(props) {
  const {
    total,
    active,
    linkConstructor,
    compact,
    isMobile,
    location,
    theme: {
      animations: {
        hover: hoverAnimation,
      },
      controls: {
        paginatron: paginatronAtoms,
      },
    },
  } = props;

  const controlHelper = {
    min: active === 1,
    max: active === total,
  };
  const isCompact = isMobile || compact;

  const {
    separationText,
    round,
    withOuterButtons,
    withoutBorder,
    btnType,
    PrevPage,
    NextPage,
    icon: {
      arrow: {
        size: arrowSize,
      },
      idle,
      hover,
      disabled,
    },
    text: {
      color,
      font,
    },
    outerButtons,
    countersMargin,
    btnSize,
  } = paginatronAtoms;

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .doubleArrow
          display block
          width ${arrowSize}
          height ${arrowSize}
      `}</style>
    </scope>
  );

  const controlDir = {
    outer: {
      className: 'outerLink',
      min: {
        href: linkConstructor(1, location),
        isOutside: controlHelper.min,
      },
      max: {
        href: linkConstructor(total, location),
        isOutside: controlHelper.max,
      },
    },

    inner: {
      className: 'innerLink',
      min: {
        href: linkConstructor(Math.max(active - 1, 1), location),
        isOutside: controlHelper.min,
      },
      max: {
        href: linkConstructor(Math.min(active + 1, total), location),
        isOutside: controlHelper.max,
      },
    },
  };

  const controlProps = (type, size) => {
    const { className } = controlDir[type];
    const { href, isOutside } = controlDir[type][size];

    return {
      href,
      isOutside,
      className,
    };
  };

  return (
    <div
      className={cx(
        'container',
        isCompact && '_compact',
        withoutBorder && '_withoutBorder',
      )}
      data-qa='lb-pagination'
    >
      <style jsx>{`
        .container
          display flex
          justify-content space-between
          align-items stretch
          overflow hidden
          &._withoutBorder
            border none

        .content
          flex auto
          display flex
          align-items center
          justify-content space-evenly
          // в IE не работает space-evenly, а так хотябы не криво
          -ms-flex-pack center

        .outerButtons
          flex none
          display flex
          align-items center

        .innerButtons
          flex none
          vertical-align bottom

        .disabled
          cursor default

        .words
          white-space nowrap
          flex 1 0 auto

        .arrow
          vertical-align bottom
      `}</style>
      <style jsx>{`
        .iconFill
          & :global([id*="$"])
            fill ${idle.fill}
            transition fill ${hoverAnimation}
          &:hover :global([id*="$"])
            fill ${hover.fill}
          &.disabled :global([id*="$"])
            fill ${disabled.fill}
        .container
          border ${outerButtons.innerBorder}
          border-radius ${paginatronAtoms.radius}
          background ${paginatronAtoms.background}
        .content
          justify-content ${paginatronAtoms.justifyContent}
        .outerButtons
          background ${outerButtons.idle.background}
          transition background ${hoverAnimation}
          &:hover
            background ${outerButtons.hover.background}
          &.disabled
           background ${outerButtons.disabled.background}
          @extend .iconFill
          &:first-child
            border-right ${outerButtons.innerBorder}
          &:last-child
            border-left ${outerButtons.innerBorder}
        .outerButtons
          color ${outerButtons.idle.color}
          &:hover
            color ${outerButtons.hover.color}
            :global(a)
              color ${outerButtons.hover.color}
          &.disabled
            color ${outerButtons.disabled.color}
        .innerButtons
          @extend .iconFill
        .text
          font ${font}
          margin ${countersMargin}
        .pages
          color ${color}
      `}</style>
      {withOuterButtons && <div
        className={cx(
          'outerButtons',
          controlHelper.min && 'disabled',
        )}
        data-qa='lb-to-first-button'
      >
        <Control {...controlProps('outer', 'min')} theme={paginatronAtoms}>
          {isCompact
            ? <DoubleArrowLeft className={scope.wrapClassNames('doubleArrow')} />
            : <div className={cx('text', 'words')}>Первая</div>
          }
        </Control>
      </div>}
      <div className='content'>
        <div
          className={cx(
            'innerButtons',
            controlHelper.min && 'disabled',
          )}
          data-qa='lb-prev-button'
        >
          <Control {...controlProps('inner', 'min')} theme={paginatronAtoms}>
            <Button
              type={btnType}
              disabled={controlHelper.min}
              round={round}
              size={btnSize}
            >
              <PrevPage />
            </Button>
          </Control>
        </div>
        <div className='text pages' data-qa='lb-counters'>
          {active}{separationText}{total}
        </div>
        <div
          className={cx(
            'innerButtons',
            controlHelper.max && 'disabled',
          )}
          data-qa='lb-next-button'
        >
          <Control {...controlProps('inner', 'max')} theme={paginatronAtoms}>
            <Button
              type={btnType}
              disabled={controlHelper.max}
              round={round}
              size={btnSize}
            >
              <NextPage />
            </Button>
          </Control>
        </div>
      </div>
      {withOuterButtons && <div
        className={cx(
          'outerButtons',
          controlHelper.max && 'disabled',
        )}
        data-qa='lb-to-last-button'
      >
        <Control {...controlProps('outer', 'max')} theme={paginatronAtoms}>
          {isCompact
            ? <DoubleArrowRight className={scope.wrapClassNames('doubleArrow')} />
            : <div className={cx('text', 'words')}>Последняя</div>
          }
        </Control>
      </div>}
      <scope.styles />
    </div>
  );
}

Paginatron.defaultProps = {
  linkConstructor: pageLinkConstructor,
};

Paginatron.propTypes = {
  /**
   * Индекс текущего состояния пагинации
   */
  active: PropTypes.number.isRequired,

  /**
   * Общее количество элементов в пагинации
   */
  total: PropTypes.number.isRequired,

  /**
   * Функция, формирует ссылку из текущего состояния active
   * @param {number} active
   * @param {object} location
   */
  linkConstructor: PropTypes.func.isRequired,

  /** Компактный режим отображения. На мобилках применяется по умолчанию. */
  compact: PropTypes.bool,

  /** @ignore */
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,

  /** @ignore */
  isMobile: PropTypes.bool,

  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },

      controls: {
        paginatron: {
          withoutBorder,
          separationText,
          round,
          withOuterButtons,
          btnType,
          NextPage,
          PrevPage,
          radius,
          justifyContent,

          text: {
            font,
            color,
          },

          outerButtons: {
            innerBorder,
            idle: {
              background,
              color,
            },
            disabled: {
              background,
              color,
            },
            hover: {
              background,
              color,
            }
          },

          icon: {
            arrow: {
              size
            },

            doubleArrow: {
              size
            },

            idle: {
              fill
            },
            hover: {
              fill
            },
            disabled: {
              fill
            }
          },
        },
      }
    }
  `),
};

const PaginatronWithHOCs = withRouter(
  withBreakpoint(
    withTheme(Paginatron)
  )
);

PaginatronWithHOCs.displayName = 'Paginatron';

export { Paginatron as StorybookComponent };
export default PaginatronWithHOCs;
